import { FC, useEffect, useRef, useState } from 'react';
import './AgentStreamingMessage.scss';
import MarkdownMessage from '../../MarkdownMessage/MarkdownMessage';
import { useChats } from '../../../providers/Chats/ChatsProvider';

interface AgentStreamingMessageProps {
  fullText: string;
  createdAt?: string;
  speed?: number;
}

function stripIncompleteHtmlTag(text: string): string {
  const lastOpen = text.lastIndexOf('<');
  if (lastOpen === -1) return text;
  const lastClose = text.lastIndexOf('>');
  if (lastClose > lastOpen) return text;
  return text.slice(0, lastOpen);
}

const AgentStreamingMessage: FC<AgentStreamingMessageProps> = ({ fullText, createdAt, speed = 15 }) => {
  const [displayedText, setDisplayedText] = useState<string>("");

  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { setChat, setIsStreaming } = useChats();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [displayedText]);

  useEffect(() => {
    if (!fullText || fullText.trim() === "") return;

    setDisplayedText("");

    let i = 0;
    const CHUNK_SIZE = fullText.includes("<table") ? 50 : 4;

    const step = () => {
      setDisplayedText(fullText.slice(0, i));
      i += CHUNK_SIZE;

      if (i <= fullText.length) {
        requestAnimationFrame(step);
      } else {
        // Quando fullText.length não é múltiplo de CHUNK_SIZE, o último passo do
        // laço acima nunca roda (o incremento já ultrapassou length) e os caracteres
        // finais ficavam de fora do slice — força o texto completo aqui.
        setDisplayedText(fullText);

        setChat((prev) => {
          if (!prev) return prev;

          const updatedMessages = [...prev?.messages ?? []];
          const lastAgentIndex = [...updatedMessages]
            .map((m, idx) => ({ ...m, idx }))
            .reverse()
            .find((m) => m.sender === "agent" && m.content === "")?.idx;

          if (lastAgentIndex === undefined) return prev;

          updatedMessages[lastAgentIndex] = {
            ...updatedMessages[lastAgentIndex],
            content: fullText,
          };

          return {
            ...prev,
            messages: updatedMessages,
          };
        });

        setIsStreaming(false);
      }
    };

    requestAnimationFrame(step);

    return () => { };
  }, [fullText, setChat, setIsStreaming]);


  return (
    <div
      className="AgentStreamingMessage AgentStreamingMessage--enter"
      data-testid="AgentStreamingMessage"
    >
      <div className="w-full">
        <MarkdownMessage content={stripIncompleteHtmlTag(displayedText)} isStreaming={displayedText.length < fullText.length} createdAt={createdAt ?? ''} />
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default AgentStreamingMessage;
